import type { ArtefactType } from '@trailblazer/shared';

/**
 * ArtefactTypeSelector — UP-F04: the three-way choice at the top of the artefact capture step,
 * File (default) / Prompt / Link. Selecting one drives which input the wizard reveals (UP-F08).
 *
 * Deliberately SMALL and self-contained (C1 owns this shell; the File input is C1's, the Prompt/Link
 * inputs are C2's — both plug into the one vm this drives). It is a segmented radiogroup built from
 * the V3 design tokens (the same border/accent-subtle treatment the Button and FormField use), so it
 * reads as part of the wizard rather than a bolted-on control. No design frame draws this — the
 * artefact path is a parallel flow with no SVG — so it is composed from primitives, not measured.
 *
 * A radiogroup, not three buttons: it is a single-choice control, so arrow keys move between options
 * and only the selected tab is in the tab order, which is what a screen reader expects here.
 */

const OPTIONS: { value: ArtefactType; label: string; hint: string }[] = [
  { value: 'file', label: 'File', hint: 'Upload a document or archive' },
  { value: 'prompt', label: 'Prompt', hint: 'Share reusable prompt text' },
  { value: 'link', label: 'Link', hint: 'Point to an https:// resource' },
];

export interface ArtefactTypeSelectorProps {
  value: ArtefactType;
  onChange: (value: ArtefactType) => void;
}

export function ArtefactTypeSelector({ value, onChange }: ArtefactTypeSelectorProps) {
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const idx = OPTIONS.findIndex((o) => o.value === value);
    if (idx < 0) return;
    let nextIdx = idx;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = (idx + 1) % OPTIONS.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      nextIdx = (idx - 1 + OPTIONS.length) % OPTIONS.length;
    else return;
    e.preventDefault();
    onChange(OPTIONS[nextIdx]!.value);
  }

  return (
    // Roving tabindex: focus lives on the selected radio (child), not the group container, so the
    // group itself is intentionally not focusable — arrow keys are handled here and move the child
    // focus. That is the WAI-ARIA radiogroup pattern, hence the interactive-supports-focus exception.
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div role="radiogroup" aria-label="Artefact type" onKeyDown={onKeyDown} className="flex gap-5">
      {OPTIONS.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(opt.value)}
            className={[
              'flex min-w-0 flex-1 flex-col gap-1 rounded-md border border-solid px-5 py-4 text-left',
              'transition-colors duration-micro ease-in-out',
              selected
                ? 'border-control bg-accent-subtle text-text'
                : 'border-border-subtle bg-transparent text-ink hover:bg-[rgb(0_157_154_/_0.1)]',
            ].join(' ')}
          >
            <span className="text-sm font-medium">{opt.label}</span>
            <span className="text-xs font-light text-ink/60">{opt.hint}</span>
          </button>
        );
      })}
    </div>
  );
}
